import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../utils/api';
import { track } from '../analytics';
import type { Order, UpsellItem, CartItem, MenuProduct, TabView } from '../types/order';

interface UseUpsellCartOptions {
  order: Order | null;
  upsellsLoading: boolean;
}

interface UseUpsellCartReturn {
  cartItems: CartItem[];
  setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  customizeUpsell: { product: UpsellItem; cartItem: CartItem | null } | null;
  setCustomizeUpsell: React.Dispatch<React.SetStateAction<{ product: UpsellItem; cartItem: CartItem | null } | null>>;
  visible: UpsellItem[];
  allCartLookup: UpsellItem[];
  total: number;
  addToCart: (productId: string, upsellItem?: UpsellItem, openCustomize?: boolean) => void;
  removeFromCart: (tempId: string) => void;
  changeQty: (tempId: string, delta: number) => void;
  getCartItemsForProduct: (productId: string) => CartItem[];
  getTotalQtyForProduct: (productId: string) => number;
  getUnitPrice: (u: UpsellItem, cust?: Record<string, string | string[]> | null) => number;
  formatCustomization: (c: Record<string, string | string[]> | null | undefined) => string;
  hasOptions: (u?: UpsellItem) => boolean;
  updateCartItemCustomization: (tempId: string, customization: Record<string, string | string[]>) => void;
  // Menu tab state and fetching
  menuProducts: MenuProduct[];
  menuLoading: boolean;
  menuError: string | null;
  menuSearchQuery: string;
  setMenuSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  menuActiveCategory: string | null;
  setMenuActiveCategory: React.Dispatch<React.SetStateAction<string | null>>;
  menuByCategory: Record<string, MenuProduct[]>;
  menuCategories: string[];
  fetchMenu: () => Promise<void>;
  activeTab: TabView;
  setActiveTab: React.Dispatch<React.SetStateAction<TabView>>;
}

export function useUpsellCart(options: UseUpsellCartOptions): UseUpsellCartReturn {
  const { order, upsellsLoading } = options;
  const { t } = useTranslation();

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [customizeUpsell, setCustomizeUpsell] = useState<{ product: UpsellItem; cartItem: CartItem | null } | null>(null);
  const [activeTab, setActiveTab] = useState<TabView>('order');
  const [menuProducts, setMenuProducts] = useState<MenuProduct[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [menuActiveCategory, setMenuActiveCategory] = useState<string | null>(null);

  const upsells = order?.upsells || [];

  const visible = useMemo(() => {
    const status = order?.status;
    // After READY, only show instant-giveout items; after COMPLETED/CANCELLED, show nothing
    if (status === 'COMPLETED' || status === 'CANCELLED') return [];
    // Keep cards visible even when the product is already in the order — user can keep adding more.
    return upsells.filter((u) => {
      const st = u.product.stockToday;
      const hasStock = st == null || (isNaN(Number(st)) || Number(st) > 0);
      if (!hasStock) return false;
      if (status === 'READY' && !u.product.instantGiveout) return false;
      return true;
    });
  }, [upsells, order?.status]);

  // allCartLookup: visible items + all recommendations (r1/r2/v1/v2) + menu products so all items count toward total
  const allCartLookup = useMemo(() => {
    const recs = order?.recommendations;
    const base = recs
      ? [
          ...visible,
          ...[
            ...(recs.r1 || []),
            ...(recs.r2 || []),
            ...(recs.v1 || []),
            ...(recs.v2 || []),
          ].filter((u) => !visible.some((v) => v.id === u.id)),
        ]
      : visible;
    const seen = new Set(base.map((u) => u.id));
    const menuAsUpsell: UpsellItem[] = menuProducts
      .filter((p) => !seen.has(p.id))
      .map((p): UpsellItem => ({
        id: p.id,
        product: {
          id: p.id,
          name: p.name,
          description: p.description,
          price: p.price,
          imageUrl: p.imageUrl,
          stockToday: p.stockToday,
          optionGroups: p.optionGroups,
        },
      }));
    return [...base, ...menuAsUpsell];
  }, [visible, order?.recommendations, menuProducts]);

  const getUnitPrice = (u: UpsellItem, cust?: Record<string, string | string[]> | null) => {
    const groups = u.product.optionGroups;
    if (!groups?.length || !cust) return u.product.price;
    let p = u.product.price;
    for (const g of groups) {
      const sel = cust[g.label];
      if (!sel) continue;
      const vals = Array.isArray(sel) ? sel : [sel];
      for (const v of vals) {
        const c = g.choices.find((x) => x.label === v);
        if (c?.priceModifier != null) p += Number(c.priceModifier);
      }
    }
    return p;
  };

  const total = cartItems.reduce((s, item) => {
    const u = allCartLookup.find((x) => x.id === item.productId);
    return u ? s + item.quantity * getUnitPrice(u, item.customization) : s;
  }, 0);

  const getCartItemsForProduct = (productId: string) =>
    cartItems.filter((c) => c.productId === productId);

  const getTotalQtyForProduct = (productId: string) =>
    getCartItemsForProduct(productId).reduce((sum, c) => sum + c.quantity, 0);

  const formatCustomization = (c: Record<string, string | string[]> | null | undefined) => {
    if (!c || !Object.keys(c).length) return '';
    return Object.entries(c).map(([, v]) => (Array.isArray(v) ? v.join(', ') : v)).filter(Boolean).join(' • ');
  };

  const hasOptions = (u?: UpsellItem) => {
    if (!u) return false;
    const og = u.product.optionGroups;
    const has = Array.isArray(og) && og.length > 0;
    return has;
  };

  const changeQty = (tempId: string, delta: number) => {
    const item = cartItems.find((c) => c.tempId === tempId);
    if (!item) return;
    const u = allCartLookup.find((x) => x.id === item.productId)
      || menuProducts.map((p): UpsellItem => ({
        id: p.id,
        product: { id: p.id, name: p.name, description: p.description, price: p.price, imageUrl: p.imageUrl, stockToday: p.stockToday },
      })).find((x) => x.id === item.productId);
    if (!u) return;
    const st = u.product.stockToday;
    const max = st != null && !isNaN(Number(st)) && Number(st) >= 0 ? Number(st) : Infinity;
    const othersQty = getTotalQtyForProduct(item.productId) - item.quantity;
    const effectiveMax = max === Infinity ? Infinity : Math.max(0, max - othersQty);
    setCartItems((prev) =>
      prev.map((c) =>
        c.tempId === tempId
          ? { ...c, quantity: Math.max(0, Math.min(effectiveMax, c.quantity + delta)) }
          : c
      ).filter((c) => c.quantity > 0)
    );
  };

  const addToCart = (productId: string, upsellItem?: UpsellItem, openCustomize = false) => {
    const tempId = `cart-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const newItem: CartItem = { tempId, productId, quantity: 1, customization: undefined };
    const opts = hasOptions(upsellItem);
    if (openCustomize && opts) {
      setCartItems((prev) => [...prev, newItem]);
      setCustomizeUpsell({ product: upsellItem!, cartItem: newItem });
    } else {
      setCartItems((prev) => [...prev, newItem]);
    }
    track('upsell_added', {
      orderId: order?.id,
      restaurantId: order?.restaurantId || order?.restaurant?.id,
      productId,
      productName: upsellItem?.product.name,
      price: upsellItem?.product?.price,
      isMenuItem: !upsellItem,
    });
  };

  const removeFromCart = (tempId: string) => {
    setCartItems((prev) => prev.filter((c) => c.tempId !== tempId));
  };

  const updateCartItemCustomization = (tempId: string, customization: Record<string, string | string[]>) => {
    setCartItems((prev) => prev.map((c) => (c.tempId === tempId ? { ...c, customization } : c)));
  };

  const fetchMenu = useCallback(async () => {
    const rid = order?.restaurantId || order?.restaurant?.id;
    if (!rid) return;
    setMenuLoading(true);
    setMenuError(null);
    try {
      const { data } = await axios.get(`${API_URL || ''}/api/products/restaurant/${rid}`);
      setMenuProducts((data || []).filter((p: MenuProduct) => p.isActive !== false));
    } catch {
      setMenuError(t('menu.loadError'));
    } finally {
      setMenuLoading(false);
    }
  }, [order, t]);

  // Lazy menu load when activeTab switches to 'menu'
  useEffect(() => {
    if (activeTab === 'menu' && menuProducts.length === 0 && !menuLoading && !upsellsLoading) {
      fetchMenu();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const menuByCategory = useMemo(() => {
    const filtered = menuProducts.filter((p) => {
      const q = menuSearchQuery.toLowerCase();
      if (q && !p.name.toLowerCase().includes(q) && !p.description?.toLowerCase().includes(q)) return false;
      if (menuActiveCategory && p.category !== menuActiveCategory) return false;
      return true;
    });
    const groups: Record<string, MenuProduct[]> = {};
    for (const p of filtered) {
      const cat = p.category || t('menu.other');
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    return groups;
  }, [menuProducts, menuSearchQuery, menuActiveCategory, t]);

  const menuCategories = useMemo(() => {
    const cats = new Set(menuProducts.map((p) => p.category || t('menu.other')));
    return Array.from(cats);
  }, [menuProducts, t]);

  return {
    cartItems,
    setCartItems,
    customizeUpsell,
    setCustomizeUpsell,
    visible,
    allCartLookup,
    total,
    addToCart,
    removeFromCart,
    changeQty,
    getCartItemsForProduct,
    getTotalQtyForProduct,
    getUnitPrice,
    formatCustomization,
    hasOptions,
    updateCartItemCustomization,
    menuProducts,
    menuLoading,
    menuError,
    menuSearchQuery,
    setMenuSearchQuery,
    menuActiveCategory,
    setMenuActiveCategory,
    menuByCategory,
    menuCategories,
    fetchMenu,
    activeTab,
    setActiveTab,
  };
}
